<template>
  <div class="landing">
    <nav class="landing-nav">
      <div class="nav-content">
        <div class="logo">
          <img src="/logo.svg" alt="ForMind" />
        </div>
        <div class="nav-actions">
          <template v-if="isAuthenticated">
            <RouterLink to="/chat" class="nav-link">{{ t('landing.nav.chat') }}</RouterLink>
            <button @click="goToProfile" class="profile-btn" :title="t('profile.profile')">
              <User :size="20" style="color: var(--text-primary)" />
            </button>
          </template>
          <template v-else>
            <RouterLink to="/login" class="nav-link">{{ t('landing.nav.login') }}</RouterLink>
            <a :href="upgradeUrl" target="_blank" rel="noopener" class="nav-btn">{{ t('landing.nav.register') }}</a>
          </template>
          <div class="lang-selector-wrapper">
            <button @click="toggleLanguageMenu" class="lang-btn" :title="t('profile.language')">
              <Globe :size="18" />
              <span class="lang-code">{{ currentLanguage?.code.toUpperCase() }}</span>
            </button>
            <div v-if="showLanguageMenu" class="lang-menu">
              <button v-for="lang in availableLocales" :key="lang.code" @click="selectLanguage(lang.code)"
                class="lang-option" :class="{ active: currentLanguage?.code === lang.code }">
                <span class="lang-code-menu">{{ lang.code.toUpperCase() }}</span>
                <span class="lang-name">{{ lang.name }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </nav>

    <section class="hero" ref="heroSection">
      <!-- <canvas ref="canvasRef" class="ai-canvas"></canvas> -->
      <div class="hero-content column-container align-center" :class="{ visible: sections.hero }">
        <div class="row-container">
          <div class="hero-badge">
            <span>{{ t('landing.hero.badge') }}</span>
          </div>
        </div>
        <div class="column-container gap-regular">
          <h1 class="hero-title">{{ t('landing.hero.title') }}</h1>
          <p class="hero-subtitle type-secondary-c">{{ t('landing.hero.subtitle') }}</p>
        </div>
        <a v-if="!isAuthenticated" :href="upgradeUrl" target="_blank" rel="noopener" class="cta-btn primary">
          <span>{{ t('landing.hero.cta') }}</span>
          <ArrowRight :size="18" style="color: var(--text-primary)" />
        </a>
        <RouterLink v-else to="/chat" class="cta-btn primary">
          <span>{{ t('landing.hero.cta') }}</span>
          <ArrowRight :size="18" style="color: var(--text-primary)" />
        </RouterLink>
        <div class="hero-image">
          <img :src="getImagePath('visual-03.png')" width="880" alt="Visual 3" class="screenshot" />
          <img :src="getImagePath('visual-02.png')" width="880" alt="Visual 2" class="screenshot" />
          <img :src="getImagePath('visual-01.png')" width="880" alt="Visual 1" class="screenshot" />
        </div>
      </div>
    </section>

    <section class="about-section" ref="aboutSection">
      <div class="about-content" :class="{ visible: sections.about }">
        <div class="about-header">
          <h2>{{ t('landing.about.title') }}</h2>
        </div>
        <div class="about-grid">
          <div class="about-card main-card">
            <div class="card-content">
              <h3>{{ t('landing.about.mainTitle') }}</h3>
              <p>{{ t('landing.about.mainDescription') }}</p>
            </div>
          </div>
          <div class="about-card secondary-card">
            <div class="card-content">
              <h3>{{ t('landing.about.summaryTitle') }}</h3>
              <p>{{ t('landing.about.summaryDescription') }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="feature-section chat-section" ref="chatSection">
      <div class="feature-content" :class="{ visible: sections.chat }">
        <div class="feature-text">
          <h2>{{ t('landing.chat.title') }}</h2>
          <p>{{ t('landing.chat.description') }}</p>
          <ul class="feature-list">
            <li><span class="check-bullet"></span>{{ t('landing.chat.feature1') }}</li>
            <li><span class="check-bullet"></span>{{ t('landing.chat.feature2') }}</li>
            <li><span class="check-bullet"></span>{{ t('landing.chat.feature3') }}</li>
            <li><span class="check-bullet"></span>{{ t('landing.chat.feature4') }}</li>
          </ul>
        </div>
        <div class="feature-images">
          <img src="/img/chat_1.png" alt="Chat Screenshot 1" class="screenshot" />
          <img src="/img/chat_2.png" alt="Chat Screenshot 2" class="screenshot" />
          <img src="/img/chat_3.png" alt="Chat Screenshot 3" class="screenshot" />
        </div>
      </div>
    </section>

    <section class="feature-section mcp-section" ref="mcpSection">
      <div class="feature-content" :class="{ visible: sections.mcp }">
        <div class="feature-text">
          <div class="code-preview">
            <pre><code>
            {
            "tools": [
            "get_stock_data",
            "analyze_financials",
            "kap_notifications",
            "technical_analysis"
            ]
            }
          </code></pre>
          </div>
          <div class="mcp-features">
            <div class="mcp-card">
              <MessageSquare :size="24" />
              <h3>{{ t('landing.mcp.card1Title') }}</h3>
              <p>{{ t('landing.mcp.card1Desc') }}</p>
            </div>
            <div class="mcp-card">
              <FileText :size="24" />
              <h3>{{ t('landing.mcp.card2Title') }}</h3>
              <p>{{ t('landing.mcp.card2Desc') }}</p>
            </div>
          </div>
        </div>
        <div class="feature-visual column-container align-start gap-large">
          <h2>{{ t('landing.mcp.title') }}</h2>
          <p>{{ t('landing.mcp.description') }}</p>
          <a href="https://mcp.forinvest.com" target="_blank" class="button">
            <FileText :size="18" />
            {{ t('landing.mcp.docs') }}
          </a>
        </div>
      </div>
    </section>

    <section class="feature-section api-section" ref="apiSection">
      <div class="feature-content" :class="{ visible: sections.api }">
        <div class="feature-text">
          <h2>{{ t('landing.api.title') }}</h2>
          <p>{{ t('landing.api.description') }}</p>
          <ul class="feature-list">
            <li><span class="check-bullet"></span>{{ t('landing.api.feature1') }}</li>
            <li><span class="check-bullet"></span>{{ t('landing.api.feature2') }}</li>
            <li><span class="check-bullet"></span>{{ t('landing.api.feature3') }}</li>
            <li><span class="check-bullet"></span>{{ t('landing.api.feature4') }}</li>
          </ul>
          <div class="row-container">
            <a v-if="!isAuthenticated" :href="upgradeUrl" target="_blank" rel="noopener" class="button">
              {{ t('landing.api.cta') }}
            </a>
            <RouterLink v-else to="/chat" class="button">{{ t('landing.api.cta') }}</RouterLink>
          </div>
        </div>
        <div class="feature-visual">
          <div class="code-preview">
            <pre><code>
            {
            "prompt": "GARAN hissesi hakkında bilgi ver",
            "model": "",
            "systemPrompt": "You are a helpful financial assistant.",
            "temperature": 0.7,
            "adaptiveThinking": true,
            "responseType": "text"
            }
          </code></pre>
          </div>
        </div>
      </div>
    </section>

    <footer class="landing-footer">
      <div class="footer-container">
        <div class="footer-logo">
          <span class="amblem-icon type-primary"></span>
        </div>
      </div>
      <div class="footer-bottom">
        <div class="footer-text">
          <p>© ForInvest Yazılım ve Teknoloji Hizmetleri A.Ş.</p>
        </div>
        <div class="social-links">
          <a href="https://www.facebook.com/forinvestcompany" target="_blank" rel="noopener" title="Facebook"
            class="social-link">
            <Facebook :size="20" style="color: var(--text-primary)" />
          </a>
          <a href="https://twitter.com/ForInvestCom" target="_blank" rel="noopener" title="X" class="social-link">
            <Twitter :size="20" style="color: var(--text-primary)" />
          </a>
          <a href="https://www.instagram.com/forinvest.app/" target="_blank" rel="noopener" title="Instagram"
            class="social-link">
            <Instagram :size="20" style="color: var(--text-primary)" />
          </a>
          <a href="https://www.linkedin.com/company/forinvestcompany/mycompany/verification/" target="_blank"
            rel="noopener" title="LinkedIn" class="social-link">
            <Linkedin :size="20" style="color: var(--text-primary)" />
          </a>
          <a href="https://www.youtube.com/@forinvestapp" target="_blank" rel="noopener" title="YouTube"
            class="social-link">
            <Youtube :size="20" style="color: var(--text-primary)" />
          </a>
        </div>
      </div>
    </footer>

    <Transition name="modal">
      <div v-if="showLoginModal" class="modal-overlay" @click="showLoginModal = false">
        <div class="modal-content" @click.stop>
          <button @click="showLoginModal = false" class="modal-close">
            <X :size="20" />
          </button>
          <LoginForm @success="handleLoginSuccess" />
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useLanguage } from '@/composables/useLanguage'
import { useAuthStore } from '@/stores/auth'
import { getAuth } from '../utils/authStorage'
import LoginForm from '@/components/LoginForm.vue'
import config from '@/config'
import { Globe, ArrowRight, MessageSquare, FileText, Facebook, Twitter, Instagram, Linkedin, Youtube, X, User } from 'lucide-vue-next'

const { t } = useI18n()
const router = useRouter()
const authStore = useAuthStore()
const { currentLanguage, availableLocales, changeLanguage } = useLanguage()
const showLoginModal = ref(false)
const showLanguageMenu = ref(false)
const isAuthenticated = computed(() => !!authStore.isAuthenticated)
const upgradeUrl = config.UPGRADE_URL
const sections = ref({
  hero: false,
  about: false,
  chat: false,
  mcp: false,
  api: false
})
const heroSection = ref<HTMLElement | null>(null)
const aboutSection = ref<HTMLElement | null>(null)
const canvasRef = ref<HTMLCanvasElement | null>(null)
const chatSection = ref<HTMLElement | null>(null)
const mcpSection = ref<HTMLElement | null>(null)
const apiSection = ref<HTMLElement | null>(null)
let animationId: number | null = null
const particles: Array<{ x: number, y: number, vx: number, vy: number }> = []

const toggleLanguageMenu = () => {
  showLanguageMenu.value = !showLanguageMenu.value
}

const selectLanguage = (code: string) => {
  changeLanguage(code)
  showLanguageMenu.value = false
}

const getImagePath = (imageName: string) => {
  if (imageName.endsWith('.png')) {
    const nameWithoutExt = imageName.replace('.png', '')
    return `/img/${nameWithoutExt}-dark.png`
  }
  return `/img/${imageName}`
}

const handleScroll = () => {
  sections.value.hero = true

  const checkSection = (element: HTMLElement | null, key: keyof typeof sections.value) => {
    if (element) {
      const rect = element.getBoundingClientRect()
      const isVisible = rect.top < window.innerHeight * 0.75
      if (isVisible) sections.value[key] = true
    }
  }

  checkSection(aboutSection.value, 'about')
  checkSection(chatSection.value, 'chat')
  checkSection(mcpSection.value, 'mcp')
  checkSection(apiSection.value, 'api')
}

function initCanvas() {
  if (!canvasRef.value || !heroSection.value) return
  const canvas = canvasRef.value
  const hero = heroSection.value
  canvas.width = hero.offsetWidth
  canvas.height = hero.offsetHeight

  for (let i = 0; i < 100; i++) {
    particles.push({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.5,
      vy: (Math.random() - 0.5) * 0.5
    })
  }
}

function animate() {
  const canvas = canvasRef.value
  if (!canvas) return

  const ctx = canvas.getContext('2d')
  if (!ctx) return

  ctx.clearRect(0, 0, canvas.width, canvas.height)

  particles.forEach(p => {
    p.x += p.vx
    p.y += p.vy
    if (p.x < 0 || p.x > canvas.width) p.vx *= -1
    if (p.y < 0 || p.y > canvas.height) p.vy *= -1
  })

  ctx.strokeStyle = 'rgba(74, 158, 255, 0.3)'
  ctx.lineWidth = 1

  for (let i = 0; i < particles.length; i++) {
    for (let j = i + 1; j < particles.length; j++) {
      const pi = particles[i]
      const pj = particles[j]
      if (!pi || !pj) continue

      const dx = pi.x - pj.x
      const dy = pi.y - pj.y
      const dist = Math.sqrt(dx * dx + dy * dy)

      if (dist < 180) {
        ctx.beginPath()
        ctx.moveTo(pi.x, pi.y)
        ctx.lineTo(pj.x, pj.y)
        ctx.globalAlpha = 1 - dist / 180
        ctx.stroke()
      }
    }
  }

  ctx.fillStyle = 'rgba(74, 158, 255, 0.6)'
  particles.forEach(p => {
    ctx.beginPath()
    ctx.arc(p.x, p.y, 2, 0, Math.PI * 2)
    ctx.fill()
  })

  animationId = requestAnimationFrame(animate)
}

onMounted(async () => {
  const userAuth = getAuth()
  
  if (userAuth?.token) {
    await authStore.validateToken()
  }
  
  setTimeout(() => sections.value.hero = true, 100)
  window.addEventListener('scroll', handleScroll)
  handleScroll()
  initCanvas()
  animate()

  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement
    if (!target.closest('.lang-selector-wrapper')) {
      showLanguageMenu.value = false
    }
  })
})

function handleLoginSuccess() {
  showLoginModal.value = false
  router.push('/chat')
}

function goToProfile() {
  router.push('/profile')
}

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
  if (animationId) cancelAnimationFrame(animationId)
})
</script>

<style scoped src="@/assets/styles/views/LandingView.css"></style>
